/**
 * Zod schema for the `personInternals` collection (ADR-136).
 *
 * Internal-only tier — readable by internal/organizer/admin. Mirrors the
 * prior `internalProfiles` schema but keyed by `personId` and accessible
 * for pre-auth persons (the prior `contacts.internalNotes` field collapses
 * into this collection).
 *
 * Doc ID matches the parent `persons` doc ID.
 *
 * See ADR-002 for the three-tier privacy architecture.
 */

import { z } from 'zod';

export const personInternalDocumentSchema = z.object({
  /** Free-text internal notes about this person. */
  notes: z.string().nullable(),

  /** Tags used to group attendees for event operations. */
  tags: z.array(z.string()),

  /**
   * PersonIds of associated companion persons. Was `companionOf` (singular)
   * on `internalProfiles`; promoted to an array to allow multi-companion
   * relationships without a schema change. Empty array for most persons.
   */
  companions: z.array(z.string()),

  /**
   * HubSpot contact record ID when this person is linked to the CRM
   * (ADR-169 Stage 2). Absent or null = unlinked. Optional so documents
   * written before the integration still validate.
   */
  hubspotContactId: z.string().nullable().optional(),

  /**
   * The linked contact's FINRA CRD number (`repcrd`) captured at link
   * time — FRG's canonical record-matching key. Stored for future
   * cross-referencing; not otherwise used in Stage 2.
   */
  hubspotRepcrd: z.string().nullable().optional(),
});
